/**
 * Tour controller - the one entry point components use.
 *
 * Owns what the engine deliberately doesn't: gating (trial Direct admins
 * only, same rule as the welcome flow) and persistence. Step position is
 * cached locally on every step; the server is only told on finish/abort -
 * one POST per run instead of one per step, and the server-side payload
 * stays small. Cross-device the resume is chapter-granular; same-device
 * it is step-exact.
 */

import Vue from "vue";
import engine from "./engine";
import { getChapter } from "./chapters";
import companyAPI from "@/api/company";
import {
  extractTourChapters,
  isTrialCompanyAdmin,
  mergeOnboardingPayload
} from "@/utils/onboarding";

// Reactive mirror of the engine's active flag. The engine's own module
// state is invisible to Vue's dependency tracking, so a computed reading
// engine.isActive() would cache forever - and the SetupGuide fab would be
// missing at the exact moment the tour's final step points at it.
var live = Vue.observable({ active: false });

function persist(updates) {
  // Logout clears the token before the /login navigation aborts the run:
  // the account-scoped storage suffix is gone and the POST cannot
  // authenticate. The suffixed cache already holds the last step from
  // onStepChange, so skip rather than pollute the shared bare key.
  if (!localStorage.getItem("token")) return Promise.resolve();
  var payload = mergeOnboardingPayload(updates);
  return companyAPI.updateOnboardingProgress(payload).catch(function() {
    // Local cache still holds it; the next successful save carries it up.
  });
}

/**
 * Start (or resume) a chapter over the real UI.
 * opts: { router (required), storeGetters (required), onDone? }
 * Returns false if gated off or unknown chapter - callers can hide their
 * launch affordances based on canTour() and treat false as "do nothing".
 */
export function startChapter(chapterId, opts) {
  if (!canTour(opts.storeGetters)) return false;
  var chapter = getChapter(chapterId);
  if (!chapter || engine.isActive()) return false;

  var saved = extractTourChapters(null)[chapterId] || {};
  var startAt =
    !saved.completed && typeof saved.step === "number" && saved.step > 0
      ? Math.min(saved.step, chapter.steps.length - 1)
      : 0;

  live.active = true;
  engine.start(chapter, {
    router: opts.router,
    startAt: startAt,
    onStepChange: function(id, index) {
      // Local only - keeps resume exact without spamming the API.
      var chapters = {};
      chapters[id] = { step: index, completed: false };
      mergeOnboardingPayload({ tourChapters: chapters });
    },
    onFinish: function(id, reason, index) {
      live.active = false;
      var chapters = {};
      var completed = reason === "completed";
      chapters[id] = {
        step: completed ? 0 : index,
        completed: completed
      };
      var updates = { tourChapters: chapters };
      // Finishing or explicitly skipping THE GUIDED TOUR (the launcher's
      // chapter, id "tour") settles the welcome-page tour flags, so /welcome
      // moves on to the wizard. Setup guide chapters never touch them.
      if (id === "tour" && completed) updates.tourCompleted = true;
      if (id === "tour" && reason === "skipped") updates.tourSkipped = true;
      persist(updates);
      if (opts.onDone) opts.onDone(id, reason);
    }
  });
  return true;
}

export function stopTour() {
  engine.stop("aborted");
}

export function tourActive() {
  // Read the reactive mirror, not engine.isActive(): inside a Vue computed
  // this creates a real dependency, so the caller re-renders on stop().
  return live.active;
}

export function canTour(storeGetters) {
  return isTrialCompanyAdmin(storeGetters);
}

export default {
  startChapter: startChapter,
  stopTour: stopTour,
  tourActive: tourActive,
  canTour: canTour
};
